import { FiFormHelperService } from '@fi-shared/fi-form-shared/fi-form-helper.service';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import {
    Component,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    ElementRef,
    ViewChild,
    ChangeDetectorRef,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { FiImageSize, FileType, ObjectTypes } from '@fi-shared/types';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { getAcceptByFileType } from '@fi-shared/utils/get-accept-by-file-type';
import { IInsertMediaResult } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiFormQuestionBase } from '@fi-app/fi-form/fi-form-edit/fi-form-edit-form/fi-form-question-base';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { isDefined } from '@fi-shared/utils';
import { Observable, Subject } from 'rxjs';
import { FiDndDetails, FiDndEventType } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-helper';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { ImageMovieFileTypes } from '@fi-shared/fi-insert-media/fi-insert-media-consts';

@Component({
    selector: 'fi-form-image-element',
    templateUrl: './fi-form-image-element.component.html',
    styleUrls: [
        './fi-form-image-element.component.scss',
        '../fi-form-text-question/fi-form-text-question.component.scss',
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [{ provide: FiComponentsContainer, useExisting: FiFormImageElementComponent }],
    standalone: false,
})
export class FiFormImageElementComponent extends FiFormQuestionBase implements OnChanges {
    @ViewChild('insertedMediaBankContainerRef', { static: false }) insertedMediaBankContainerRef: ElementRef;
    FiImageSize = FiImageSize;

    currenMediaBankFileReferences: IMediaBankFileReference[] = [];

    constructor(
        elementRef: ElementRef,
        fiBrowserEventsService: FiBrowserEventsService,
        fiFormHelperService: FiFormHelperService,
        private fiInsertMedia2Service: FiInsertMedia2Service,
        private fileUpload2Service: FiFileUpload2Service,
        private cdr: ChangeDetectorRef,
    ) {
        super(fiBrowserEventsService, elementRef, fiFormHelperService);
    }

    ngOnChanges(changes: SimpleChanges): void {
        let change = changes.selectedLanguageId;
        if (change) {
            this.updateCurrenMediaBankFileReferences();
        }
    }

    uploadFiles(files: File[]): void {
        files.length = 1;
        this.fileUpload2Service
            .silentUploadFiles(files, { contentLanguageId: this.selectedLanguageId })
            .then((mediaBankReferences: IMediaBankFileReference[]) => {
                this.updateQuestionMediaBankFileReferences(mediaBankReferences);
            });
    }

    insertFile() {
        this.fiInsertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.Form, {
                multiple: false,
                accept: getAcceptByFileType([FileType.Image, FileType.Movie], undefined),
                fileTypes: [FileType.Image, FileType.Movie],
                contentLanguageId: this.selectedLanguageId,
            })
            .onYesButtonClick((result: IInsertMediaResult) => {
                this.updateQuestionMediaBankFileReferences(result.mediaBankFileReferences);
            });
    }

    updateQuestionMediaBankFileReferences(newMediaBankFileReferences: IMediaBankFileReference[]) {
        // we should remove exising media references with selectedContentLanguageId and then add new ones
        this.question.MediaBankFileReferences = this.question.MediaBankFileReferences.filter(
            (mbRef) =>
                !this.fiFormHelperService.isMediaBankFileReferenceForSelectedLanguage(mbRef, this.selectedLanguageId),
        ).concat(newMediaBankFileReferences);

        this.updateCurrenMediaBankFileReferences();

        this.cdr.detectChanges();
    }

    private _showInsertFileErrorTooltip$ = new Subject<void>();
    protected get showInsertFileErrorTooltip$(): Observable<void> {
        return this._showInsertFileErrorTooltip$.asObservable();
    }
    protected onDndDetails($event: FiDndDetails) {
        if ($event.dndEventType === FiDndEventType.DragEnter && !$event.isDndAllowed) {
            this._showInsertFileErrorTooltip$.next();
        }
    }

    private updateCurrenMediaBankFileReferences() {
        this.currenMediaBankFileReferences = this.question.MediaBankFileReferences.filter((mbRef) =>
            this.fiFormHelperService.isMediaBankFileReferenceForSelectedLanguage(mbRef, this.selectedLanguageId),
        );
    }

    protected readonly FiTooltipContentType = FiTooltipContentType;
    protected readonly ImageMovieFileTypes = ImageMovieFileTypes;
}
